import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Boxes, Truck, PackageOpen, Siren, Warehouse, ClipboardList } from "lucide-react";
import { IMG } from "@/lib/site";
import { Reveal, LineReveal } from "@/components/cinema/Reveal";

const services = [
  { icon: ClipboardList, title: "Contents Assessment & Inventory", desc: "Detailed documentation and inventory preparation that supports restoration projects and insurance claims.", img: IMG.contentsAssessment },
  { icon: Boxes, title: "Pack-Out Services", desc: "Professional packing, labeling and transportation to protect belongings during restoration.", img: IMG.packout },
  { icon: PackageOpen, title: "Pack-Back Services", desc: "Careful unpacking and placement of contents once repairs are complete.", img: IMG.packback },
  { icon: Siren, title: "Emergency Restoration", desc: "Rapid response for water, fire, storm and property damage to stabilize affected areas.", img: IMG.emergencyOps },
  { icon: Warehouse, title: "Secure Storage Solutions", desc: "Climate-considered storage for personal and commercial contents during long projects.", img: IMG.storage },
  { icon: Truck, title: "Professional Moving", desc: "Coordinated residential and commercial moving with the same care our restoration work demands.", img: IMG.movingTruck },
];

const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-");

// Background photo crossfades to the hovered/focused service.
export default function ServicesChapter() {
  const [active, setActive] = useState(3);

  return (
    <section className="chapter" data-testid="services-overview" data-active-service={active}>
      <div className="chapter-bg" aria-hidden>
        {services.map((s, i) => (
          <motion.div
            key={s.title}
            className="chapter-img"
            style={{ backgroundImage: `url(${s.img})` }}
            initial={false}
            animate={{ opacity: i === active ? 1 : 0, scale: i === active ? 1.04 : 1.12 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          />
        ))}
      </div>
      <div className="chapter-overlay chapter-overlay-dark" aria-hidden />
      <div className="grain absolute inset-0" aria-hidden />

      <div className="chapter-content container-gaza w-full py-28 md:py-36">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div className="max-w-3xl">
            <Reveal y={16}><div className="eyebrow eyebrow-light mb-5">What we do</div></Reveal>
            <LineReveal
              className="display-chunky text-4xl md:text-6xl text-white leading-[0.92]"
              lines={["Six services.", <span className="text-[hsl(var(--gaza-crimson-bright))]">One ruthless</span>, "commitment to recovery."]}
            />
          </div>
          <Reveal delay={0.4}>
            <Link to="/services" className="hidden md:inline-flex items-center gap-2 font-extrabold uppercase tracking-[0.18em] text-sm text-[hsl(var(--gaza-blue-bright))] link-underline" data-testid="services-all-link">
              View all services <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>

        <div className="border-t border-white/15">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08} y={20}>
              <Link
                to="/services"
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                className={`group grid md:grid-cols-12 gap-4 md:gap-8 items-center py-6 md:py-7 border-b border-white/15 transition-colors md:px-4 md:-mx-4 ${i === active ? "bg-white/[0.05]" : "hover:bg-white/[0.04]"}`}
                data-testid={`service-card-${slug(s.title)}`}
              >
                <span className="md:col-span-1 text-xs font-extrabold tracking-[0.22em] text-white/40 group-hover:text-[hsl(var(--gaza-crimson-bright))]">/0{i + 1}</span>
                <div className="md:col-span-5 flex items-center gap-4">
                  <s.icon className="w-6 h-6 shrink-0 text-[hsl(var(--gaza-crimson-bright))]" strokeWidth={2} />
                  <h3 className={`display-chunky text-2xl md:text-3xl xl:text-4xl leading-tight transition-colors ${i === active ? "text-[hsl(var(--gaza-blue-bright))]" : "text-white"}`}>{s.title}</h3>
                </div>
                <p className="md:col-span-5 text-white/70 leading-relaxed">{s.desc}</p>
                <ArrowRight className="hidden md:block md:col-span-1 justify-self-end w-5 h-5 text-white/40 group-hover:text-white group-hover:translate-x-1 transition" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
